import React, { useState, useEffect } from "react";
import {
  Tab,
  Button,
  Icon,
  Dropdown,
} from "semantic-ui-react";
import { BasicDrawer } from "../../../components/Shared";
import {
  ListEnergyForms,
  EnergyForm,
  UnitsForm,
} from "../../../components/Admin/EnergyForms";
import { useAuth } from "../../../hooks";
import { Form } from "../../../api";
import { decrypt } from "../../../utils/cryptoUtils";
import { useLocation, useNavigate } from "react-router-dom";
import {
  isAdmin,
  isMaster,
  isEditor,
} from "../../../utils/checkPermission";
import "./EnergyForms.scss";
import { useLanguage, useSite } from "../../../contexts";
import { useCompany } from "../../../contexts/CompanyContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// `text` sale de las traducciones (t(key)) al renderizar, no acá — estos
// keys ("water", "effluent", etc.) ya existen en los .json de i18n.
const QUESTIONNAIRE_OPTIONS = [
  { key: "water",       value: "/admin/data/waterforms" },
  { key: "effluent",    value: "/admin/data/effluentforms" },
  // { key: "air",          value: "/admin/data/airemissionsforms" },
  { key: "energy",      value: "/admin/data/energyforms" },
  // { key: "envobjetives", value: "/admin/data/environmentalobjetives" },
  { key: "production",  value: "/admin/data/productionforms" },
  { key: "waste",       value: "/admin/data/wasteforms" },
  { key: "social",      value: "/admin/data/socialforms" },
  { key: "fuel",        value: "/admin/data/fuelsforms" },
];

const formController = new Form();

export function EnergyForms() {
  const [showModal, setShowModal] = useState(false);
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [reload, setReload] = useState(false);
  const navigate = useNavigate();
  const {
    accessToken,
    user: { role, site, company },
  } = useAuth();
  const [siteSelected, setSiteSelected] = useState(null);
  const [configFormData, setConfigFormData] = useState([]);

  const [yearSelected, setYearSelected] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);
  const onOpenCloseUnitsModal = () => setShowUnitsModal((prevState) => !prevState);
  const onReload = () => setReload((prevState) => !prevState);

  const validateUnitsConfigured = () => {
    if (!configFormData?.fields || configFormData.fields.length === 0) {
      return { valid: true };
    }

    const activeFields = configFormData.fields.filter(field => field.active !== false);

    if (activeFields.length === 0) {
      return { valid: false, message: t("no_active_fields") };
    }

    const fieldsWithUnit = activeFields.filter(field => field.unit && field.unit !== "");

    if (fieldsWithUnit.length === 0) {
      return {
        valid: false,
        message: t("units_not_configured") || "Debe configurar al menos una unidad antes de agregar un periodo. Haga clic en el ícono de tres puntos (⋯) en la columna de Unidades."
      };
    }

    return { valid: true };
  };

  const handleOpenNewForm = () => {
    const validation = validateUnitsConfigured();
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }
    onOpenCloseModal();
  };

  const { translations, language } = useLanguage();

  const t = (key) => translations[key] || key;

  const location = useLocation();

  const siteContext = useSite().site;

  const companyContext = useCompany().company;

  useEffect(() => {
    (async () => {
      try {
        if (siteContext !== "") {
          setSiteSelected(siteContext);
        }
      } catch (error) {
        console.error(error);
        setSiteSelected(null);
      }
    })();
  }, [siteContext]);

  useEffect(() => {
    (async () => {
      try {
        if (site) {
          setSiteSelected(site);
        }
      } catch (error) {
        console.error(error);
        setSiteSelected(null);
      }
    })();
  }, [site]);

  useEffect(() => {
    (async () => {
      try {
        if (location.state?.siteSelected) {
          setSiteSelected(decrypt(location.state.siteSelected));
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [location]);

  useEffect(() => {
    (async () => {
      try {
        if (
          siteSelected &&
          siteSelected !== "" &&
          siteSelected !== "undefined"
        ) {
          const response = await formController.getFormsBySite(
            accessToken,
            siteSelected,
            "energy"
          );

          if (response.length > 0) {
            setConfigFormData(response[0]);
          }
        }
      } catch (error) {
        console.error(error);
        setConfigFormData([]);
      }
    })();
  }, [siteSelected, accessToken, reload]);

  const handleSaveUnits = async (units) => {
    try {
      const fieldsToUpdate = Object.keys(units).map((fieldName) => ({
        code: fieldName,
        name: fieldName,
        unit: units[fieldName],
      }));

      await formController.updateFormFields(
        accessToken,
        configFormData._id,
        fieldsToUpdate
      );

      const response = await formController.getFormsBySite(
        accessToken,
        siteSelected,
        "energy"
      );

      if (response.length > 0) {
        setConfigFormData(response[0]);
      }

      onReload();
    } catch (error) {
      console.error("Error saving units:", error);
    }
  };

  const currentUnits = configFormData?.fields?.reduce((acc, field) => {
    acc[field.name] = field.unit;
    return acc;
  }, {}) || {};

  const panes = [
    {
      render: () => (
        <Tab.Pane attached={false}>
          {(siteSelected === null || siteSelected === "") &&
          (isMaster(role) || (isAdmin(role) && !site)) ? (
            <div className="no-site-warning">
              <Icon name="warning sign" size="big" />
              <p>{t("select_company_site_from_header")}</p>
            </div>
          ) : (
            <ListEnergyForms
              reload={reload}
              onReload={onReload}
              siteSelected={siteSelected}
              yearSelected={yearSelected}
              configFormData={configFormData}
            />
          )}
        </Tab.Pane>
      ),
    },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

  return (
    <>
      <div className="energy-forms-page">
        <div className="energy-page-header-wrapper">
          <div className="energy-page-header">
            <Dropdown
              className="energy-questionnaire-dropdown"
              options={QUESTIONNAIRE_OPTIONS
                .map((opt) => ({ ...opt, text: t(opt.key) }))
                .sort((a, b) => a.text.localeCompare(b.text, language))}
              selection
              onChange={(_, data) => navigate(data.value)}
              value="/admin/data/energyforms"
            />
            <div className="energy-page-actions">
              {(siteSelected !== null || site) ? (
                <Dropdown
                  className="energy-year-dropdown"
                  placeholder={t("select")}
                  options={years.map((year) => ({ key: year, text: year, value: year }))}
                  selection
                  onChange={(_, data) => setYearSelected(data.value)}
                  value={yearSelected}
                />
              ) : null}
            </div>
          </div>
          {(siteSelected !== null || site) && (isAdmin(role) || isMaster(role) || isEditor(role)) ? (
            <Button icon className="energy-add-btn" onClick={handleOpenNewForm}>
              <Icon name="plus" />
            </Button>
          ) : null}
        </div>

        <Tab menu={{ secondary: true }} panes={panes} />
      </div>

      <BasicDrawer
        show={showModal}
        close={onOpenCloseModal}
        title={t("energy_form")}
      >
        <EnergyForm
          onClose={onOpenCloseModal}
          onReload={onReload}
          year={yearSelected}
          siteSelected={siteSelected}
          configFormData={configFormData}
          unitsRecorded={currentUnits}
        />
      </BasicDrawer>

      <UnitsForm
        show={showUnitsModal}
        onClose={onOpenCloseUnitsModal}
        onSave={handleSaveUnits}
        currentUnits={currentUnits}
      />
    </>
  );
}

